class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.debounceTimer = null;
    this.abortController = null;

    this.onInputBound = this.onInput.bind(this);
    this.onFocusBound = this.onFocus.bind(this);
    this.onClearBound = this.onClear.bind(this);
    this.onDocumentClickBound = this.onDocumentClick.bind(this);
    this.onKeyDownBound = this.onKeyDown.bind(this);
    this.onSubmitBound = this.onSubmit.bind(this);
    this.onRecentClickBound = this.onRecentClick.bind(this);
    this.recentSearchesKey = "rsl:recent-searches:v1";
    this.maxRecentSearches = 4;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.input = this.querySelector(".predictive-search-input");
    this.resultsContainer = this.querySelector(".predictive-search-results-list");
    this.suggestionsContainers = this.querySelectorAll(".js-suggestions-list");
    this.categoriesContainers = this.querySelectorAll(".js-categories-list");
    this.brandsContainers = this.querySelectorAll(".js-brands-list");
    this.suggestionSections = this.querySelectorAll("[data-search-suggestions-section]");
    this.categorySections = this.querySelectorAll("[data-search-categories-section]");
    this.brandSections = this.querySelectorAll("[data-search-brands-section]");
    this.itemsCount = this.querySelector(".js-items-count");
    this.viewAllQuery = this.querySelector(".js-view-all-query");
    this.clearBtn = this.querySelector(".js-clear-btn");
    this.recentSection = this.querySelector("[data-search-recent-section]");
    this.recentList = this.querySelector("[data-search-recent-list]");
    this.recentItemTemplate = this.querySelector("[data-recent-item-template]");
    this.enableSuggestions = this.hasAttribute("data-enable-suggestions");
    this.rootUrl = window.Shopify?.routes?.root || window.theme?.routes?.rootUrl || "/";

    this.form?.addEventListener("submit", this.onSubmitBound);
    this.input?.addEventListener("input", this.onInputBound);
    this.input?.addEventListener("focus", this.onFocusBound);
    this.input?.addEventListener("keydown", this.onKeyDownBound);
    this.clearBtn?.addEventListener("click", this.onClearBound);
    this.recentSection?.addEventListener("click", this.onRecentClickBound);
    document.addEventListener("click", this.onDocumentClickBound);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmitBound);
    this.input?.removeEventListener("input", this.onInputBound);
    this.input?.removeEventListener("focus", this.onFocusBound);
    this.input?.removeEventListener("keydown", this.onKeyDownBound);
    this.clearBtn?.removeEventListener("click", this.onClearBound);
    this.recentSection?.removeEventListener("click", this.onRecentClickBound);
    document.removeEventListener("click", this.onDocumentClickBound);
    this.abortController?.abort();
  }

  onDocumentClick(e) {
    if (!this.contains(e.target)) {
      this.close();
    }
  }

  onKeyDown(e) {
    if (e.key === "Escape") {
      this.close();
      this.input?.focus();
    }
  }

  onInput(e) {
    const query = e.target.value.trim();

    if (query.length > 0) {
      this.classList.add("is-typing");
    } else {
      this.classList.remove("is-typing");
    }

    clearTimeout(this.debounceTimer);

    if (query.length < 1) {
      this.renderRecentSearches();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.search(query);
    }, 300);
  }

  onFocus() {
    const query = this.input.value.trim();
    if (query.length < 1) {
      this.renderRecentSearches();
      return;
    }

    // Re-run the search so the panel state (results/discovery) is rebuilt.
    // open() alone only sets is-open, but the panel needs is-results/is-recent to show.
    this.search(query);
  }

  focusSearch() {
    this.input?.focus();
    const query = this.input?.value?.trim() || "";
    if (query.length >= 1) {
      this.open();
    }
  }

  onClear() {
    this.input.value = "";
    this.classList.remove("is-typing");
    this.renderRecentSearches();
    this.input.focus();
  }

  onSubmit() {
    const query = this.input?.value?.trim() || "";
    if (query.length >= 1) {
      this.saveRecentSearch(query);
    }
  }

  onRecentClick(event) {
    const removeButton = event.target.closest?.("[data-search-recent-remove]");
    if (removeButton) {
      event.preventDefault();
      this.removeRecentSearch(removeButton.dataset.searchRecentRemove);
      this.renderRecentSearches();
      this.input?.focus();
      return;
    }

    const clearButton = event.target.closest?.("[data-search-recent-clear]");
    if (clearButton) {
      event.preventDefault();
      this.clearRecentSearches();
      this.close();
      this.input?.focus();
    }
  }

  async search(query) {
    const searchUrl = window?.theme?.routes?.predictiveSearchUrl;
    if (!searchUrl) return;

    this.abortController?.abort();
    this.abortController = new AbortController();

    this.open();
    this.classList.add("is-loading");
    this.classList.remove(
      "is-recent",
      "is-results",
      "is-empty",
      "is-discovery-show",
    );

    try {
      const requests = [
        fetch(
          `${searchUrl}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku&section_id=predictive-search`,
          { signal: this.abortController.signal },
        ),
      ];

      if (this.enableSuggestions) {
        requests.push(
          fetch(
            `${this.rootUrl}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=query,collection&resources[limit]=5`,
            { signal: this.abortController.signal },
          ),
        );
      }

      const responses = await Promise.all(requests);

      if (!responses[0].ok) throw new Error("Search failed");

      const text = await responses[0].text();
      const html = new DOMParser().parseFromString(text, "text/html");
      const products = html.querySelectorAll(".predictive-search__product-item");

      let suggestions = [];
      let collections = [];
      if (this.enableSuggestions && responses[1]?.ok) {
        const suggestData = await responses[1].json();
        suggestions = suggestData.resources?.results?.queries || [];
        collections = suggestData.resources?.results?.collections || [];
      }

      this.renderResults(Array.from(products), suggestions, collections, query);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Predictive search error:", error);
        this.close();
      }
    }
  }

  renderResults(products, suggestions, collections, query) {
    this.foldedNeedle = this.fold((query || "").trim());
    this.hideRecentSearches();
    this.classList.remove("is-loading");
    this.classList.add("is-results");

    const brands = this.extractBrands(products);
    const hasProducts = products.length > 0;
    const hasSuggestions = suggestions.length > 0;
    const hasCollections = collections.length > 0;
    const hasBrands = brands.length > 0;
    const hasDiscovery = hasSuggestions || hasCollections || hasBrands;

    if (!hasProducts && !hasDiscovery) {
      this.classList.add("is-empty");
      this.dataset.count = "0";
      this.dataset.hasResults = "false";
      this.resultsContainer.innerHTML = "";
      this.clearLists(this.suggestionsContainers);
      this.clearLists(this.categoriesContainers);
      this.clearLists(this.brandsContainers);
      this.toggleSections(this.suggestionSections, false);
      this.toggleSections(this.categorySections, false);
      this.toggleSections(this.brandSections, false);
      if (this.itemsCount) {
        this.itemsCount.textContent = "0";
      }
      return;
    }

    this.classList.remove("is-empty");
    this.dataset.hasResults = "true";
    this.classList.toggle("is-discovery-show", hasDiscovery);

    if (hasSuggestions) {
      this.toggleSections(this.suggestionSections, true);
      this.renderSuggestions(suggestions);
    } else {
      this.toggleSections(this.suggestionSections, false);
      this.clearLists(this.suggestionsContainers);
    }

    this.renderCollections(collections);
    this.renderBrands(brands);

    if (hasProducts) {
      this.dataset.count = products.length.toString();

      if (this.itemsCount) {
        this.itemsCount.textContent = products.length;
      }

      const fragment = document.createDocumentFragment();
      products.forEach((product) => {
        fragment.appendChild(document.importNode(product, true));
      });
      this.resultsContainer.innerHTML = "";
      this.resultsContainer.appendChild(fragment);
    } else {
      this.dataset.count = "0";
      this.resultsContainer.innerHTML = "";
      if (this.itemsCount) {
        this.itemsCount.textContent = "0";
      }
    }

    if (this.viewAllQuery) {
      this.viewAllQuery.textContent = query;
    }
  }

  renderSuggestions(suggestions) {
    this.renderLinkList(this.suggestionsContainers, suggestions, (suggestion) => {
      const text = this.getSuggestionText(suggestion);
      return {
        text,
        href: suggestion.url || `${this.rootUrl}search?q=${encodeURIComponent(text)}&type=product`,
      };
    });
  }

  renderRecentSearches() {
    const searches = this.getRecentSearches();

    if (!this.recentSection || !this.recentList || !this.recentItemTemplate || searches.length === 0) {
      this.close();
      return;
    }

    this.abortController?.abort();
    clearTimeout(this.debounceTimer);
    this.resultsContainer.innerHTML = "";
    this.clearLists(this.suggestionsContainers);
    this.clearLists(this.categoriesContainers);
    this.clearLists(this.brandsContainers);
    this.toggleSections(this.suggestionSections, false);
    this.toggleSections(this.categorySections, false);
    this.toggleSections(this.brandSections, false);

    this.classList.remove(
      "is-loading",
      "is-results",
      "is-empty",
      "is-discovery-show",
    );
    this.classList.add("is-recent");
    this.dataset.count = "0";
    this.dataset.hasResults = "false";

    if (this.itemsCount) {
      this.itemsCount.textContent = "0";
    }

    this.recentList.innerHTML = "";
    searches.forEach((term) => {
      this.recentList.appendChild(this.createRecentSearchItem(term));
    });

    this.recentSection.classList.remove("hidden");
    this.open();
  }

  hideRecentSearches() {
    this.classList.remove("is-recent");
    this.recentSection?.classList.add("hidden");
  }

  createRecentSearchItem(term) {
    const node = this.recentItemTemplate.content.firstElementChild.cloneNode(true);

    const link = node.querySelector("[data-recent-link]");
    link.href = `${this.rootUrl}search?q=${encodeURIComponent(term)}&type=product`;
    link.textContent = term;

    const removeButton = node.querySelector("[data-search-recent-remove]");
    removeButton.dataset.searchRecentRemove = term;
    const removeLabel = removeButton.getAttribute("aria-label");
    if (removeLabel) {
      // Function replacer: term is user input, so avoid $-pattern substitution in the label.
      removeButton.setAttribute("aria-label", removeLabel.replace("{{term}}", () => term));
    }

    return node;
  }

  getRecentSearches() {
    try {
      const searches = JSON.parse(localStorage.getItem(this.recentSearchesKey) || "[]");
      if (!Array.isArray(searches)) return [];

      return searches
        .map((term) => (typeof term === "string" ? term.trim() : ""))
        .filter(Boolean)
        .slice(0, this.maxRecentSearches);
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(query) {
    const term = query.trim();
    if (!term) return;

    try {
      const searches = this.getRecentSearches();
      const nextSearches = [term, ...searches.filter((item) => item.toLowerCase() !== term.toLowerCase())].slice(
        0,
        this.maxRecentSearches,
      );

      localStorage.setItem(this.recentSearchesKey, JSON.stringify(nextSearches));
    } catch (error) {
      // Ignore storage failures so search submit remains unaffected.
    }
  }

  removeRecentSearch(term) {
    if (!term) return;

    try {
      const nextSearches = this.getRecentSearches().filter((item) => item !== term);
      localStorage.setItem(this.recentSearchesKey, JSON.stringify(nextSearches));
    } catch (error) {
      // Ignore storage failures so the search overlay remains usable.
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem(this.recentSearchesKey);
    } catch (error) {
      // Ignore storage failures so the search overlay remains usable.
    }
  }

  renderCollections(collections) {
    this.toggleSections(this.categorySections, collections.length > 0);
    this.renderLinkList(this.categoriesContainers, collections, (collection) => {
      const text = this.getSuggestionText(collection);
      return {
        text,
        href: collection.url || `${this.rootUrl}search?q=${encodeURIComponent(text)}&type=product`,
      };
    });
  }

  renderBrands(brands) {
    this.toggleSections(this.brandSections, brands.length > 0);
    this.renderLinkList(this.brandsContainers, brands, (brand) => ({
      text: brand,
      href: `${this.rootUrl}search?q=${encodeURIComponent(brand)}&type=product`,
    }));
  }

  renderLinkList(containers, items, normalize) {
    containers.forEach((container) => {
      container.innerHTML = "";

      items.forEach((item) => {
        const { text, href } = normalize(item);
        if (!text) return;

        const li = document.createElement("li");
        li.className = "predictive-search__list-item";

        const a = document.createElement("a");
        a.href = href;
        a.className = "predictive-search__list-link";
        this.highlightMatch(a, text);

        li.appendChild(a);
        container.appendChild(li);
      });
    });
  }

  highlightMatch(el, text) {
    el.textContent = "";
    const needle = this.foldedNeedle;
    // Fold accents so an unaccented query matches accented text and vice versa; map keeps the
    // marked slice pointing at the original (accented) characters.
    const { folded, map } = this.foldText(text);
    if (!needle || !folded.includes(needle)) {
      el.textContent = text;
      return;
    }

    let from = 0;
    let index = folded.indexOf(needle);
    while (index !== -1) {
      const start = map[index];
      const end = map[index + needle.length];
      if (start > from) {
        el.appendChild(document.createTextNode(text.slice(from, start)));
      }
      const mark = document.createElement("mark");
      mark.className = "predictive-search__highlight";
      mark.textContent = text.slice(start, end);
      el.appendChild(mark);
      from = end;
      index = folded.indexOf(needle, index + needle.length);
    }
    if (from < text.length) {
      el.appendChild(document.createTextNode(text.slice(from)));
    }
  }

  fold(text) {
    return (text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }

  foldText(text) {
    let folded = "";
    const map = [];
    let orig = 0;
    for (const ch of text || "") {
      const f = this.fold(ch);
      for (let i = 0; i < f.length; i += 1) map.push(orig);
      folded += f;
      orig += ch.length;
    }
    map.push(orig);
    return { folded, map };
  }

  getSuggestionText(suggestion) {
    return suggestion.title || suggestion.text || suggestion.styled_text || suggestion.query || "";
  }

  extractBrands(products) {
    const seen = new Set();
    const needle = this.foldedNeedle;

    return products
      .map((product) => product.dataset.vendor || product.querySelector("[data-search-vendor]")?.textContent || "")
      .map((brand) => brand.trim())
      .filter((brand) => {
        if (!brand) return false;
        const key = brand.toLowerCase();
        if (seen.has(key)) return false;
        // Fold accents so the filter matches the same brands highlightMatch will highlight.
        if (needle && !this.fold(brand).includes(needle)) return false;
        seen.add(key);
        return true;
      });
  }

  clearLists(containers) {
    containers.forEach((container) => {
      container.innerHTML = "";
    });
  }

  toggleSections(sections, isVisible) {
    sections.forEach((section) => {
      section.classList.toggle("hidden", !isVisible);
    });
  }

  open() {
    this.classList.add("is-open");
    this.input?.setAttribute("aria-expanded", "true");
  }

  close() {
    clearTimeout(this.debounceTimer);
    this.classList.remove(
      "is-open",
      "is-loading",
      "is-recent",
      "is-results",
      "is-empty",
      "is-discovery-show",
    );
    this.recentSection?.classList.add("hidden");
    this.input?.setAttribute("aria-expanded", "false");
    this.abortController?.abort();
  }
}

customElements.define("predictive-search", PredictiveSearch);
